import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
} from '@roger/ui-components';
import { Check, Edit, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  getAgencyConfig,
  updateAgencyConfig,
} from '../../services/agencyConfig';

/**
 * Agency policy for advancing a chart without a signature (ROG-3268).
 *
 * This is the agency's call, not a rollout gate, which is why it sits on the
 * agency Admin Panel rather than Internal Admin and why it is stored on
 * AgencyConfig rather than behind a feature flag. It ships on, matching the
 * Kinnser behaviour our customers are migrating from; an agency turns it off.
 */
const ChartWorkflowSection: React.FC = () => {
  const { agencyId, showSuccess, showError } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // A read that failed tells us nothing about this agency's setting, so the
  // card must not offer to write one. Without this the screen would report the
  // product default as if it were the agency's stored answer, and an admin
  // could save a value derived from a failed read.
  const [loadFailed, setLoadFailed] = useState(false);
  const [allowSubmitWithoutSignature, setAllowSubmitWithoutSignature] =
    useState(true);
  // Last known server value. The edit state is compared against it so Cancel
  // has something true to restore, and so a no-op save writes nothing.
  const [savedValue, setSavedValue] = useState(true);

  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const cfg = await getAgencyConfig();
        if (cancelled) return;
        const value = Boolean(cfg.allow_submit_without_signature);
        setAllowSubmitWithoutSignature(value);
        setSavedValue(value);
      } catch (err) {
        console.error('Failed to load agency config:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  const handleCancel = () => {
    setAllowSubmitWithoutSignature(savedValue);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (allowSubmitWithoutSignature === savedValue) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      // Only this field. The same endpoint also carries the Intake Manager
      // column layout, which this screen never edits and must not reset.
      await updateAgencyConfig({
        allow_submit_without_signature: allowSubmitWithoutSignature,
      });
      setSavedValue(allowSubmitWithoutSignature);
      setIsEditing(false);
      showSuccess('Chart workflow settings updated');
    } catch (err) {
      console.error('Failed to update agency config:', err);
      setAllowSubmitWithoutSignature(savedValue);
      showError('Failed to update chart workflow settings');
    } finally {
      setIsSaving(false);
    }
  };

  const showDisplayView = !isEditing;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chart Workflow</CardTitle>
        <CardDescription>
          How charts move between clinicians and case managers
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className='space-y-6'>
          <div className='flex items-center justify-between gap-4 rounded-md border p-3'>
            <div className='space-y-0.5'>
              <label
                htmlFor='allow_submit_without_signature'
                className='text-sm font-medium'
              >
                Submit without signing
              </label>
              <p className='text-xs text-muted-foreground'>
                Clinicians can submit a chart before signing it. Case managers
                can return one without approving. The signature is still owed.
              </p>
              {loadFailed && (
                <p className='text-xs text-destructive'>
                  This setting did not load. Reload the page to see it.
                </p>
              )}
            </div>
            {isLoading ? (
              <Loader2
                className='h-4 w-4 shrink-0 animate-spin text-muted-foreground'
                aria-label='Loading setting'
              />
            ) : loadFailed ? (
              <span className='shrink-0 whitespace-nowrap text-sm font-medium text-muted-foreground'>
                Unavailable
              </span>
            ) : !showDisplayView ? (
              <Switch
                id='allow_submit_without_signature'
                checked={allowSubmitWithoutSignature}
                onCheckedChange={setAllowSubmitWithoutSignature}
              />
            ) : (
              <span
                className={`shrink-0 whitespace-nowrap text-sm font-medium ${
                  allowSubmitWithoutSignature
                    ? 'text-green-600'
                    : 'text-muted-foreground'
                }`}
              >
                {allowSubmitWithoutSignature ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>

          <div className='flex justify-end gap-2'>
            {isEditing ? (
              <>
                <Button
                  variant='outline'
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className='mr-2 h-4 w-4' />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant='outline'
                onClick={() => setIsEditing(true)}
                disabled={isLoading || loadFailed}
              >
                <Edit className='mr-2 h-4 w-4' />
                Edit
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartWorkflowSection;
